import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaStore, FaCode, FaChevronLeft, FaChevronRight, FaDownload, FaImage, FaSearchPlus } from 'react-icons/fa';
import { IoRestaurant } from 'react-icons/io5';
import MenuItemCard from './MenuItemCard';
import { MenuCardsSkeleton } from './MenuCardsSkeleton';
import { JsonEditor } from '../common/JsonEditor';
import { ImageModal } from '../common/ImageModal';
import { MenuTypeDropdown } from './MenuTypeDropdown';
import { useDownloadJson } from '../../hooks/useDownloadJson';
import type { MenuItem } from '../../services/menuService';

interface Restaurant {
  restaurantId: string;
  name: string;
  address: string;
  phone: string | null;
  cuisineType: string | null;
  image: string | null;
}

interface MenuPanelProps {
  selectedRestaurant: Restaurant | null;
  menuItems: MenuItem[];
  loading: boolean;
  menuStatus?: any;
  menuType: 'food' | 'drinks';
  onMenuTypeChange: (type: 'food' | 'drinks') => void;
}

export const MenuPanel: React.FC<MenuPanelProps> = ({
  selectedRestaurant,
  menuItems,
  loading,
  menuStatus,
  menuType,
  onMenuTypeChange
}) => {
  const [activeTab, setActiveTab] = useState<'image' | 'menu' | 'json'>('image');
  const [isExpanded, setIsExpanded] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const { download } = useDownloadJson();

  // Parse restaurant images from JSON string
  const getRestaurantImages = (): string[] => {
    if (!selectedRestaurant?.image) return [];
    
    try {
      const parsed = JSON.parse(selectedRestaurant.image);
      // const parsed = ['https://d1csarkz8obe9u.cloudfront.net/posterpreviews/restaurant-daily-food-menu-card-free-delivery-design-template-155aa3802541c781da01ea00facbeb6d_screen.jpg?ts=1734184848','https://design-assets.adobeprojectm.com/content/download/express/public/urn:aaid:sc:VA6C2:752b4aef-96a1-4c7e-8f42-c48f7d851cb6/rendition?assetType=TEMPLATE&etag=d62a397b1355cecbf8f901b377007471&size=500&type=image/png&fragment=id=38dbcf65-8e3e-4cb2-82a2-47197b762d3a','https://5.imimg.com/data5/SELLER/Default/2023/7/327860090/HT/LI/RZ/48567411/restaurant-menu-card-printing-service.jpg']

      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Failed to parse restaurant images:', error);
      return [];
    }
  };

  const restaurantImages = getRestaurantImages();

  // Reset failed images when restaurant changes
  useEffect(() => {
    setFailedImages(new Set());
  }, [selectedRestaurant?.restaurantId]);

  const handleDownloadJson = () => {
    if (!menuItems || menuItems.length === 0) return;

    // Generate filename with restaurant name and EST timestamp
    const estDate = new Date().toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    // Convert MM/DD/YYYY to YYYY-MM-DD format
    const [month, day, year] = estDate.split('/');
    const timestamp = `${year}-${month}-${day}`;
    
    const restaurantName = selectedRestaurant?.name.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'menu';
    const filename = `menu-${restaurantName}-${timestamp}.json`;

    // Use the reusable download utility
    download({
      data: menuItems,
      filename,
      space: 2
    });
  };

  if (!selectedRestaurant) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-8">
        <div className="text-center">
          <FaStore className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] opacity-30" />
          <p className="text-lg font-medium text-[var(--text-secondary)]">Select a merchant to view menu</p>
          <p className="text-sm text-[var(--text-muted)] mt-2">Choose a merchant from the list to view their menu items and information.</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      animate={{
        width: isExpanded ? 'calc(100vw - 128px)' : 'auto',
        marginLeft: isExpanded ? '-316px' : '0px'
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="flex-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 shadow-sm flex flex-col h-full overflow-hidden relative"
    >
      {/* Expand/Collapse Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#EF4421] text-white flex items-center justify-center shadow-lg hover:bg-[#FF6B4A] transition-colors"
        aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
      >
        {isExpanded ? <FaChevronRight className="w-4 h-4" /> : <FaChevronLeft className="w-4 h-4" />}
      </button>

      {/* Tabs Header */}
      <div className="flex items-center mb-4 flex-shrink-0 ml-10 space-x-3">
        {/* Menu Type Dropdown - NO BORDER */}
        <MenuTypeDropdown
          value={menuType}
          onChange={onMenuTypeChange}
        />
        
        {/* Tabs + Badges Container - WITH BORDER */}
        <div className="flex items-center justify-between flex-1 border-b border-[var(--border-color)]">
          {/* Tabs */}
          <div className="flex items-center space-x-1">
          <button
            onClick={() => setActiveTab('image')}
            className={`px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'image'
                ? 'text-[#EF4421] border-b-2 border-gray-300'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            <FaImage className="w-4 h-4" />
            <span>Image Source</span>
          </button>
          <button
            onClick={() => setActiveTab('menu')}
            className={`px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'menu'
                ? 'text-[#EF4421] border-b-2 border-gray-300'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            <IoRestaurant className="w-4 h-4" />
            <span>Items</span>
            {menuItems.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#EF4421] text-white font-medium">
                {menuItems.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('json')}
            className={`px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'json'
                ? 'text-[#EF4421] border-b-2 border-gray-300'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            <FaCode className="w-4 h-4" />
            <span>JSON</span>
          </button>
          </div>
          
          {/* Right Side Badges */}
          <div className="flex items-center space-x-2">
            {/* Menu Upload Iterations Badge - Only visible when count > 0 */}
            {menuStatus?.menu_upload_count > 0 && (
              <div className="px-3 py-1.5 text-sm font-medium bg-[#EF4421] text-white rounded-md flex items-center space-x-2 shadow-sm mb-0.5">
                <span>Menu Upload Iterations:</span>
                <span className="font-bold">{menuStatus.menu_upload_count}</span>
              </div>
            )}
            
            {/* Download JSON Button - Only visible when JSON tab is active */}
            {activeTab === 'json' && menuItems.length > 0 && (
              <button
                onClick={handleDownloadJson}
                className="px-3 py-1.5 text-sm font-medium bg-[#EF4421] text-white rounded-md hover:bg-[#FF6B4A] transition-colors flex items-center space-x-2 shadow-sm mb-0.5"
                aria-label="Download JSON"
              >
                <FaDownload className="w-3.5 h-3.5" />
                <span>Download JSON</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="overflow-y-auto flex-1 min-h-0">
        {activeTab === 'image' ? (
          // Image Tab Content
          restaurantImages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FaImage className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] opacity-30" />
                <p className="text-lg font-medium text-[var(--text-secondary)]">No image uploaded</p>
                <p className="text-sm text-[var(--text-muted)] mt-2">This merchant has no uploaded image</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4 p-2">
              {restaurantImages.map((imageUrl, index) => (
                <div
                  key={index}
                  className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="aspect-video relative bg-gray-100 flex items-center justify-center group">
                    {failedImages.has(index) ? (
                      <div className="flex flex-col items-center justify-center space-y-2 p-4">
                        <FaImage className="w-12 h-12 text-[var(--text-muted)] opacity-40" />
                        <p className="text-sm text-[var(--text-secondary)] font-medium">Unable to load image</p>
                      </div>
                    ) : (
                      <>
                        <img
                          src={imageUrl}
                          alt={`${selectedRestaurant?.name} - Image ${index + 1}`}
                          className="w-full h-full object-contain"
                          onError={() => {
                            setFailedImages(prev => new Set(prev).add(index));
                          }}
                        />
                        {/* Search Icon Overlay */}
                        <div 
                          className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          onClick={() => setSelectedImageIndex(index)}
                        >
                          <FaSearchPlus className="w-10 h-10 text-white" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : loading ? (
          <MenuCardsSkeleton />
        ) : menuItems.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              {activeTab === 'menu' ? (
                <>
                  <IoRestaurant className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] opacity-30" />
                  <p className="text-lg font-medium text-[var(--text-secondary)]">No menu items found</p>
                  <p className="text-sm text-[var(--text-muted)] mt-2">This merchant has no menu items yet</p>
                </>
              ) : (
                <>
                  <FaCode className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] opacity-30" />
                  <p className="text-lg font-medium text-[var(--text-secondary)]">No data available</p>
                  <p className="text-sm text-[var(--text-muted)] mt-2">This merchant has no menu data</p>
                </>
              )}
            </div>
          </div>
        ) : activeTab === 'menu' ? (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2">
            {menuItems.map((item) => (
              <MenuItemCard key={item.pk_id} item={item} />
            ))}
          </div>
        ) : (
          <JsonEditor
            value={menuItems}
            readOnly={true}
            height="calc(100vh - 280px)"
            showFormatButton={false}
            showCopyButton={false}
            theme="light"
          />
        )}
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <ImageModal 
            images={restaurantImages}
            currentIndex={selectedImageIndex}
            onClose={() => setSelectedImageIndex(null)}
            onNext={() => setSelectedImageIndex(prev => prev !== null && prev < restaurantImages.length - 1 ? prev + 1 : prev)}
            onPrevious={() => setSelectedImageIndex(prev => prev !== null && prev > 0 ? prev - 1 : prev)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};
